// src/types/moodleTypes.ts

// ============================================================================
// CORE MOODLE DATA TYPES
// ============================================================================

export interface MoodleCourse {
  id: number;
  fullname: string;
  shortname: string;
  summary: string;
  summaryformat?: number;
  categoryid?: number;
  visible?: number;
  startdate?: number;
  enddate?: number;
  [key: string]: any;
}

export interface MoodleActivity {
  id: number;
  name: string;
  modname: string;
  instance: number;
  url?: string;
  modicon?: string;
  description?: string;
  visible: number;
  contextid?: number;
  component?: string;
  filearea?: string;
  itemid?: number;
  filepath?: string;
  completiondata?: any;
  [key: string]: any;
}

export interface MoodleSection {
  id: number;
  name: string;
  section: number;
  summary: string;
  summaryformat?: number;
  visible?: number;
  modules: MoodleActivity[];
  [key: string]: any;
}

export interface MoodleModule {
  id: number;
  name: string;
  modname: string;
  instance: number;
  modicon?: string;
  description?: string;
  visible: number;
  contextid?: number;
  component?: string;
  filearea?: string;
  itemid?: number;
  filepath?: string;
  completiondata?: any;
  [key: string]: any;
}

export interface MoodleUser {
  id: number;
  email?: string;
  firstname?: string;
  lastname?: string;
  fullname?: string;
  username?: string;
  [key: string]: any;
}

export interface MoodleFile {
  filename: string;
  filepath: string;
  mimetype: string;
  url: string;
  fileurl?: string;
  filesize?: number;
  timemodified?: number;
  [key: string]: any;
}

export interface MoodleFilesResponse {
  files: MoodleFile[];
  warnings?: any[];
  [key: string]: any;
}

// ============================================================================
// FORUM TYPES
// ============================================================================

export interface MoodleForum {
  id: number;
  course?: number;
  coursemodule?: number;
  name?: string;
  shortname?: string;
  intro?: string;
  introformat?: number;
  type?: string;
  assessed?: number;
  assesstimestart?: number;
  assesstimefinish?: number;
  scale?: number;
  maxbytes?: number;
  maxattachments?: number;
  forcesubscribe?: number;
  trackingtype?: number;
  rsstype?: number;
  rssarticles?: number;
  timemodified?: number;
  warnafter?: number;
  blockafter?: number;
  blockperiod?: number;
  completiondiscussions?: number;
  completionreplies?: number;
  completionposts?: number;
  [key: string]: any;
}

export interface MoodleForumDiscussion {
  id: number;
  forum?: number;
  name?: string;
  subject?: string;
  message?: string;
  messageformat?: number;
  pinned?: boolean | number;
  locked?: boolean | number;
  favourite?: boolean | number;
  userid?: number;
  userfullname?: string;
  usermodified?: number;
  timemodified?: number;
  created?: number;
  timestart?: number;
  timeend?: number;
  groupid?: number;
  [key: string]: any;
}

export interface MoodleForumPost {
  id: number;
  discussion?: number;
  parent?: number | null;
  subject?: string;
  message?: string;
  messageformat?: number;
  userid?: number;
  userfullname?: string;
  author?: { id?: number; fullname?: string } | string;
  created?: number;
  modified?: number;
  children?: MoodleForumPost[];
  [key: string]: any;
}

export interface MoodleForumAccessInfo {
  subscribed?: boolean;
  cansubscribe?: boolean;
  canadddiscussion?: boolean;
  canreply?: boolean;
  canviewdiscussion?: boolean;
  [key: string]: any;
}

// ============================================================================
// FEEDBACK TYPES
// ============================================================================

export interface MoodleFeedbackResponseItem {
  id: number;
  name: string;
  printval: string;
  rawval: string;
}

export interface MoodleFeedbackAttempt {
  id: number;
  courseid: number;
  userid: number;
  timemodified: number;
  fullname: string;
  responses: MoodleFeedbackResponseItem[];
}

export interface MoodleFeedbackResponsesAnalysis {
  attempts: MoodleFeedbackAttempt[];
  totalattempts: number;
  anonattempts: any[];
  totalanonattempts: number;
  warnings: any[];
}

// ============================================================================
// COURSE COMPLETION TYPES
// ============================================================================

export interface MoodleCourseCompletionUser {
  user: any;
  activities_completion: {
    statuses: any[];
    warnings: any[];
  };
}

// ============================================================================
// CATEGORY & TAG TYPES
// ============================================================================

export type MoodleCategory = {
  id: number;
  name: string;
  description?: string;
  descriptionformat?: number;
  parent?: number;
  sortorder?: number;
  coursecount?: number;
  visible?: number;
  visibleold?: number;
  timemodified?: number;
  depth?: number;
  path?: string;
  [key: string]: any;
};

export interface MoodleTag {
  name: string;
  viewurl: string;
  isstandard: boolean;
  count: number;
  size: number;
}

export interface MoodleTagCollection {
  id: number;
  name: string;
  isdefault: boolean;
  component: string;
}

export interface MoodleTagArea {
  id: number;
  component: string;
  itemtype: string;
  enabled: boolean;
  tagcollid: number;
  callback: string | null;
  callbackfile: string | null;
  showstandard: number;
  multiplecontexts: boolean;
  locked: boolean;
}

// ============================================================================
// DEPLOYMENT MANAGEMENT TYPES
// ============================================================================

export interface MoodleDeployment {
  course_id: number;
  moodle_url: string;
  deployed_at: string;
  deployed_by?: number;
  status: 'active' | 'failed' | 'syncing' | 'pending';
  last_synced?: string;
  version?: number;
  error_message?: string;
}

export type DeploymentEnvironment = 'draft' | 'staging' | 'production';

// ============================================================================
// SNACKABLE COURSE CONTENT TYPES
// ============================================================================

export interface SnackableContentBlock {
  type:
    | 'heading'
    | 'paragraph'
    | 'list'
    | 'callout'
    | 'card'
    | 'code'
    | 'table'
    | 'note'
    | 'warning'
    | 'image'
    | 'video';
  text?: string;
  content?: string;
  level?: number;
  items?: string[];
  ordered?: boolean;
  language?: string;
  title?: string;
  style?: 'info' | 'success' | 'warning' | 'error';
  color?: string;
  rows?: string[][];
  headers?: string[];
  src?: string;
  alt?: string;
  caption?: string;
  styles?: {
    padding?: string;
    background?: string;
    border_radius?: string;
    width?: string;
    height?: string;
    font_size?: string;
    [key: string]: any;
  };
}

export interface SnackablePage {
  name: string;
  content: SnackableContentBlock[];
  section: number;
  visible?: boolean;
  display?: number;
}

export interface SnackableQuizQuestion {
  name: string;
  questiontext: string;
  questiontype?: string;
  choices: string[];
  correctanswer: number;
  feedback?: string;
  generalfeedback?: string;
  defaultmark?: number;
}

export interface SnackableQuiz {
  name: string;
  intro: SnackableContentBlock[];
  questions: SnackableQuizQuestion[];
  section: number;
  timelimit?: number;
  attempts?: number;
  grademethod?: number;
  visible?: boolean;
}

export interface SnackableForum {
  name: string;
  intro: SnackableContentBlock[];
  section: number;
  type?: string;
  visible?: boolean;
}

export interface SnackableResource {
  name: string;
  reference: string;
  section: number;
  description?: string;
  visible?: boolean;
}

export interface SnackableFeedback {
  name: string;
  intro: SnackableContentBlock[];
  section: number;
  questions?: any[];
  visible?: boolean;
}

export interface SnackableSectionMeta {
  name: string;
  summary?: string;
  visible?: boolean;
  section?: number;
}

// ============================================================================
// COMPLETE SNACKABLE COURSE DATA STRUCTURE
// ============================================================================

export interface SnackableCourseStructure {
  fullname: string;
  shortname: string;
  summary: string;
  categoryid?: number;
  sections: SnackableSectionMeta[];
  pages: SnackablePage[];
  quizzes: SnackableQuiz[];
  forums: SnackableForum[];
  resources: SnackableResource[];
  feedback?: SnackableFeedback[];
}

export interface SnackableCourseData {
  id?: number;
  fullname: string;
  shortname: string;
  summary: string;
  categoryid?: number;

  // Draft structure (JSON before any deployment)
  draft_course?: SnackableCourseStructure | null;

  // Staging deployment (separate Moodle instance)
  staging_deployment?: MoodleDeployment | null;

  // Production deployment (separate Moodle instance)
  production_deployment?: MoodleDeployment | null;

  // Legacy backend fields
  staged_course?: SnackableCourseStructure | null;
  deployed_course?: SnackableCourseStructure | null;
  staged_moodle_id?: number | null;
  deployed_moodle_id?: number | null;
  reference_type?: string;
  reference_id?: number;
  title?: string;
  last_updated?: string;
  updated_by?: string | number;
  created?: string;

  // Version/resource generation context
  product_id?: number | null;
  product_name?: string | null;
  product_version_id?: number | null;
  product_version_name?: string | null;
  product_license_id?: number | null;
  product_license_name?: string | null;
  doc_version?: string | null;
  deployment_scope?: string | null;
  generation_scope?: string | null;
  version_source?: string | null;
  version_context?: {
    product_id?: number | null;
    product_name?: string | null;
    product_version_id?: number | null;
    product_version_name?: string | null;
    product_license_id?: number | null;
    product_license_name?: string | null;
    doc_version?: string | null;
    deployment_scope?: string | null;
    generation_scope?: string | null;
    version_source?: string | null;
    [key: string]: any;
  } | null;

  // Legacy support
  sections?: SnackableSectionMeta[];
  pages?: SnackablePage[];
  quizzes?: SnackableQuiz[];
  forums?: SnackableForum[];
  resources?: SnackableResource[];
  feedback?: SnackableFeedback[];

  // Metadata
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  version?: number;
}

// ============================================================================
// CACHE KEYS
// ============================================================================

export enum CacheKeyMoodle {
  MoodleUser = 'moodle-user',
  MoodleCourses = 'moodle-courses',
  MoodleCourseContent = 'moodle-course-content',
  MoodleCourseEnrollments = 'moodle-course-enrolments',
  MoodleFiles = 'moodle-files',
}
